import styled from "@emotion/styled";
import {
  BLACK,
  BLUE_CREEK,
  COOL_BLUE,
  COOL_GREY,
  MIDDLE_GREY,
  RED_CREEK,
  WHITE,
} from "../../styles/colors";
import { UNSELECTABLE_TEXT } from "../../styles/styles";
import { css } from "@emotion/react";

const sz = (v?: number | string) =>
  v == null ? undefined : typeof v === "number" ? `${v}px` : v;

/* ---------- root ---------- */

export const Root = styled("div")`
  position: relative;
  display: inline-block;
  overflow: visible !important;
`;

/* ---------- trigger button ---------- */

export const Button = styled("button")<{
  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;
}>`
  width: ${({ controlWidth }) => sz(controlWidth) || "auto"};
  max-width: ${({ controlMaxWidth }) => sz(controlMaxWidth) || "100%"};
  height: ${({ controlHeight }) => sz(controlHeight)};

  display: grid;
  grid-template-columns: 1fr ${({ iconSize }) => sz(iconSize) || "24px"};
  align-items: center;
  column-gap: 10px;

  padding: 8px 10px;
  border: 2px solid #90caf9;
  border-radius: 4px;
  background: ${WHITE};
  color: ${BLUE_CREEK};
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  /* focus-visible, not focus: closing the panel refocuses the trigger. */
  &:focus-visible {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
  }

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ButtonBody = styled("div")`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-right: 10px;
`;

export const ButtonText = styled("span")<{ isPlaceholder?: boolean }>`
  min-width: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  user-select: none;

  ${({ isPlaceholder }) =>
    isPlaceholder &&
    `
      color: ${BLUE_CREEK};
      text-transform: uppercase;
    `}
`;

export const RequiredMarker = styled("span")`
  &::after {
    content: " *";
    font-size: 16px;
    color: ${RED_CREEK};
    margin-left: 1px;
  }
`;

export const FilterIcon = styled("span")<{ iconSize?: number | string }>`
  width: ${({ iconSize }) => sz(iconSize) || "24px"};
  height: ${({ iconSize }) => sz(iconSize) || "24px"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${BLUE_CREEK};
  user-select: none;

  & > img {
    width: 100%;
    height: 100%;

    /* The span owns the click; an img would start a native drag instead. */
    pointer-events: none;
  }
`;

/* ---------- panel ---------- */

export const Panel = styled("div")<{
  minW?: number;
  maxW?: number | string;
}>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 9999;

  width: max-content;
  min-width: ${({ minW }) => (minW ? `${minW}px` : "320px")};
  max-width: ${({ maxW }) => sz(maxW)};

  background: ${WHITE};
  border: 1px solid ${COOL_GREY};
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 8px;
  box-sizing: border-box;
`;

/* ---------- search ---------- */

export const Search = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
`;

export const SearchFieldWrapper = styled("div")`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;

  & > input {
    width: 100%;
    padding: 6px 28px 6px 8px;
    border: 1px solid ${COOL_GREY};
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
    box-sizing: border-box;
    outline: none;

    &:focus {
      border-color: ${BLUE_CREEK};
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
    }
  }
`;

/** The in-control search. The control draws the border, so the input brings none of its own. */
export const ControlSearch = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  cursor: text;
`;

export const ControlSearchInput = styled("input")`
  width: 100%;
  min-width: 0;
  /* Room on the right for the clear button. */
  padding: 0 22px 0 0;
  border: none;
  background: transparent;
  outline: none;
  font: inherit;
  color: ${BLACK};
  box-sizing: border-box;
`;

export const ClearButton = styled("button")`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: ${MIDDLE_GREY};
  width: 18px;
  height: 18px;
  padding: 0;
  cursor: pointer;

  &:hover {
    background: ${COOL_BLUE};
    color: ${BLUE_CREEK};
  }
`;

/* ---------- list ---------- */

export const Divider = styled("div")`
  border-top: 1px solid ${COOL_GREY};
  margin: 6px 0;
`;

export const OptionsScroll = styled("div")<{ panelMaxHeight?: number }>`
  max-height: ${({ panelMaxHeight }) =>
    panelMaxHeight != null ? `${panelMaxHeight}px` : "240px"};
  overflow: auto;

  display: flex;
  flex-direction: column;
`;

export const Option = styled("div")`
  padding: 4px 2px;
`;

export const Check = styled("label")`
  display: flex;
  align-items: center;
  padding: 1px 0;
  font-size: 14px;
  gap: 10px;
  color: ${BLACK};
  cursor: pointer;
  user-select: none;
  line-height: 1;

  input[type="checkbox"] {
    margin: 0;
    flex-shrink: 0;
    transform: translateY(-1px);
  }

  span {
    line-height: 1;
  }
`;

export const Empty = styled("div")`
  padding: 8px;
  color: ${MIDDLE_GREY};
  font-style: italic;
`;

export const Toolbar = styled("div")`
  display: flex;

  /* Baseline, not center: these items are different font sizes, so centring their
     boxes puts the text on three different baselines. */
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  min-width: 0;

  /* Block flow, not inline-flex: a flex container's baseline would come from the
     checkbox rather than the text. */
  label {
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  label input[type="checkbox"] {
    margin: 0 6px 0 0;
    vertical-align: middle;
  }

  .count {
    color: ${MIDDLE_GREY};
    font-size: 12px;
    white-space: nowrap;
  }

  .action-btn {
    margin-left: auto;
    border: none;
    background: transparent;
    color: ${BLUE_CREEK};
    font-size: 13px;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    white-space: nowrap;

    &:hover {
      background: ${COOL_BLUE};
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
`;

export const Filter = css`
  width: 16px;
  height: 16px;
  cursor: pointer;
  ${UNSELECTABLE_TEXT}
`;
